import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {mapParseItem, mapParseList, TRANSLATE, zNumberToString} from '@brunata-angular/util';
import {SelectableItem} from '@brunata-angular/ui';
import {Sorting} from '@brunata-angular/ui';
import {BehaviorSubject, map, Observable} from 'rxjs';
import {environment} from 'src/environments/environment';
import {z} from 'zod';
import {
    VariableAlarm,
    VariableAlarmEdit,
    variableAlarmSchema,
    VariableAlarmSubmit,
} from './variable-alarm.model';
import {
    VariableAlarmTypeEligibility,
    variableAlarmTypeEligibilitySchema,
} from './create-variable-alarm/variable-alarm-type-eligibility.model';
import {
    MonitoringMeterBase,
    monitoringMeterBaseSchema,
    MonitoringMeterWithTranslations,
} from 'src/app/monitoring/monitoring-meter.model';

export type FilterData = {
    alloUnitType: SelectableItem[] | null;
    unit: SelectableItem[] | null;
    alarmName: string | null;
};

@Injectable({
    providedIn: 'root',
})
export class VariableAlarmsService {
    private readonly translate = inject(TRANSLATE);

    sorting = new BehaviorSubject<Sorting>({column: 'alarmName', direction: 'asc'});

    page = new BehaviorSubject<number>(1);
    pageSize = new BehaviorSubject<number>(20);

    filterSubject: BehaviorSubject<FilterData | null> = new BehaviorSubject<FilterData | null>(
        null,
    );

    baseUrl = `${environment.onlineServiceUrl}/buildings`;

    constructor(private http: HttpClient) {}

    setFilterColumns(filterFormValues: FilterData): void {
        this.filterSubject.next(filterFormValues);
    }

    updateSorting(sorting: Sorting): void {
        this.sorting.next(sorting);
    }

    getVariableAlarms(buildingNo: string): Observable<VariableAlarm[]> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/alarms`, {
                withCredentials: true,
            })
            .pipe(
                mapParseItem(
                    z
                        .object({
                            variableAlarms: z.array(variableAlarmSchema),
                        })
                        .transform(data => data.variableAlarms),
                ),
            );
    }

    getEligibleVariableAlarmTypes(buildingNo: string): Observable<VariableAlarmTypeEligibility> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/alarms/variable/eligibletypes`, {
                withCredentials: true,
            })
            .pipe(mapParseItem(variableAlarmTypeEligibilitySchema));
    }

    createVariableAlarm(
        buildingNo: string,
        variableAlarm: VariableAlarmSubmit,
    ): Observable<string> {
        return this.http
            .post(`${this.baseUrl}/${buildingNo}/alarms/variable`, variableAlarm, {
                withCredentials: true,
            })
            .pipe(map(response => zNumberToString.parse(response)));
    }

    deleteVariableAlarm(buildingNo: string, variableAlarm: VariableAlarm): Observable<unknown> {
        return this.http.delete(
            `${this.baseUrl}/${buildingNo}/alarms/variable/${variableAlarm.deviationNotificRuleId}`,
            {
                withCredentials: true,
            },
        );
    }

    getVariableAlarm(
        buildingNo: string,
        deviationnotificruleid: string,
    ): Observable<VariableAlarm> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/alarms/variable/${deviationnotificruleid}`, {
                withCredentials: true,
            })
            .pipe(map(response => variableAlarmSchema.parse(response)));
    }

    editVariableAlarm(
        buildingNo: string,
        deviationnotificruleid: string,
        variableAlarm: VariableAlarmEdit,
    ): Observable<any> {
        return this.http.put(
            `${this.baseUrl}/${buildingNo}/alarms/variable/${deviationnotificruleid}`,
            variableAlarm,
            {
                withCredentials: true,
            },
        );
    }

    // getEligibleMeters uses the same schema as ConsumptionAgentService.getMetersForAgent
    getEligibleMeters(
        buildingNo: string,
        deviationNotificRuleId?: string,
    ): Observable<MonitoringMeterWithTranslations[]> {
        let url = `${this.baseUrl}/${buildingNo}/alarms/variable/eligiblemeters`;
        if (deviationNotificRuleId != null) {
            url = `${url}?deviationnotificruleid=${deviationNotificRuleId}`;
        }
        return this.http
            .get(url, {
                withCredentials: true,
            })
            .pipe(mapParseList(monitoringMeterBaseSchema), this.addTranslationsForMeter());
    }

    addTranslationsForMeter(): (
        source$: Observable<MonitoringMeterBase[]>,
    ) => Observable<MonitoringMeterWithTranslations[]> {
        return (source$: Observable<MonitoringMeterBase[]>) =>
            source$.pipe(
                map(meters =>
                    meters.map(meter => {
                        const allocationUnit = this.translate(
                            'mappers_allocationUnitMap.' + meter.alloUnitType,
                            'online',
                            'common',
                        );

                        const unit = this.translate(
                            'mappers_measurementUnit.' + meter.unit,
                            'online',
                            'common',
                        );
                        const usageUnitTranslated = allocationUnit + ' - ' + unit;

                        return {...meter, usageAndUnit: usageUnitTranslated};
                    }),
                ),
            );
    }
}
